import {
  Button,
  DataGrid,
  DataGridColumn,
  DataGridState,
  PageContainer,
  StatusOption,
  useToast,
} from '@sl/react-datafabric-components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentContext } from '../../../../common/context/agent.provider';
import { useFetchAgentFeedsQuery, useSendFeedToWritingMutation } from './redux';

// Feeds that didn't become a candidate — operators can force these into the
// writing pipeline (bypasses the relevance/dedup gate).
const REPROCESSABLE = ['discard', 'error', 'duplicated'];

export const statusOptions: StatusOption[] = [
  { value: 'ai_processed', label: 'AI Processed', severity: 'success' },
  { value: 'candidate', label: 'Candidate', severity: 'info' },
  { value: 'duplicated', label: 'Duplicated', severity: 'warning' },
  {
    value: 'discard',
    label: 'Discard',
    severity: 'danger',
  },
  { value: 'error', label: 'Error', severity: 'danger' },
];

export default function AgentFeedsPage() {
  const navigate = useNavigate();
  const { currentAgentId } = useAgentContext();
  const [tableState, setTableState] = useState<
    DataGridState<{ queryString: string; agent?: string }>
  >({
    rows: 20,
    page: 1,
    first: 0,
    multiSort: [{ field: 'createdAt', order: -1 }],
    filters: {
      queryString: '',
      agent: currentAgentId,
    },
  });
  const { data: agentFeedsData, isFetching } = useFetchAgentFeedsQuery(
    tableState,
    {
      skip: !currentAgentId,
    }
  );

  const { showToast } = useToast();
  const [sendFeedToWriting] = useSendFeedToWritingMutation();
  const [sendingId, setSendingId] = useState<string | null>(null);

  const handleSendToWriting = async (rowData: any, ev?: any) => {
    // Don't let the click bubble to the row (which navigates to the detail).
    ev?.stopPropagation?.();
    if (!rowData?.id) return;
    setSendingId(rowData.id);
    try {
      const taskID =
        (globalThis.crypto as any)?.randomUUID?.() ??
        `feed-${rowData.id}-${Date.now()}`;
      const res: any = await sendFeedToWriting({
        feedID: rowData.id,
        taskID,
      }).unwrap();
      if (res?.success === false) {
        throw new Error(res?.message || 'Failed to send feed to writing.');
      }
      showToast(
        {
          severity: 'success',
          summary: 'Sent to writing',
          detail:
            'Feed queued for research & writing — the article will appear under Articles shortly.',
        },
        'top-right'
      );
    } catch (e: any) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail:
            e?.data?.message || e?.message || 'Failed to send feed to writing.',
        },
        'bottom-right'
      );
    } finally {
      setSendingId(null);
    }
  };

  useEffect(() => {
    setTableState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        agent: currentAgentId,
      },
    }));
  }, [currentAgentId]);

  const columns: DataGridColumn<any>[] = [
    {
      field: 'createdAt',
      header: 'Date',
      sortable: true,
      type: 'date',
      style: { width: '125px' },
    },
    { field: 'title', header: 'Title', sortable: true },
    { field: 'source', header: 'Source', sortable: true },
    {
      field: 'status',
      header: 'Status',
      sortable: true,
      type: 'status',
      readOnly: true,
      statusOptions,
    },
    {
      field: 'relevance_rating',
      header: 'Relevance',
      sortable: true,
      type: 'numeric',
    },
    // The discard gate actually cuts on `publish_rating` (the classifier's
    // should-be-published score), NOT `relevance_rating` shown above — so a
    // row can show Relevance 6 yet be discarded at publish_rating 5. Surface
    // the real decision field, with the classifier's reasoning on hover.
    {
      field: 'publish_rating',
      header: 'Publish score',
      sortable: true,
      type: 'numeric',
      body: (rowData: any) => {
        const pr = rowData?.publish_rating;
        const score = typeof pr === 'number' ? pr : '—';
        const why: string = rowData?.ai_comments || '';
        return (
          <span
            title={why ? `Why: ${why}` : undefined}
            style={{
              cursor: why ? 'help' : undefined,
              textDecoration: why ? 'underline dotted' : undefined,
            }}
          >
            {score}
          </span>
        );
      },
    },
    {
      field: '',
      header: '',
      body: (rowData: any) => {
        if (!REPROCESSABLE.includes(rowData?.status)) return null;
        return (
          <div className='flex justify-content-end'>
            <Button
              icon='pi pi-send'
              label='Send to writing'
              tooltip='Force this feed into the writing pipeline (bypasses the relevance/dedup gate)'
              onClick={(ev) => handleSendToWriting(rowData, ev)}
              loading={sendingId === rowData.id}
              size='small'
              text
            />
          </div>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <DataGrid
        dataKey='id'
        enableGlobalSearch
        globalSearchField='queryString'
        onRowClick={(ev) => navigate(`/news-agents/feeds/${ev.data.id}`)}
        value={agentFeedsData?.data ?? []}
        entityLabel='Agent Feeds'
        columns={columns}
        gridState={tableState}
        onFiltersUpdate={setTableState}
        totalRecords={agentFeedsData?.meta?.itemCount ?? 0}
        loading={isFetching}
        paginator
        dateFilter
        mainStatusFilter={{
          name: 'status',
          options: statusOptions,
        }}
      />
    </PageContainer>
  );
}
